import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BadgeList from '../../components/BadgeList';
import { determineUserBadges } from '../../utils/badgeUtils';
import { getAllUsers, getSeedContentHidden } from '../../services/firestoreService';
import { SEED_MEMBERS } from '../../data/seedMembers';
import 'react-tooltip/dist/react-tooltip.css';

const MemberDirectory = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage] = useState(8);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await getAllUsers(100);
      // Filter to only approved members and map to display format
      const approvedMembers = usersData
        .filter(user => (user.status === 'approved' && user.role !== 'admin') || user.isFoundingMember === true)
        .map(user => ({
          id: user.id,
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.displayName || 'Member',
          title: user.title || user.jobTitle || '',
          company: user.company || user.organization || '',
          location: user.location || user.city || '',
          specialty: user.specialty || user.industry || '',
          tier: user.membershipTier || '',
          image: user.profilePhoto || user.photoURL || user.profileImage || '',
          memberSince: user.memberSince || (user.createdAt?.toDate ? user.createdAt.toDate().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : ''),
          isFoundingMember: user.isFoundingMember || false,
          isVipMember: user.isVipMember || false,
          role: user.role || '',
          referralCount: user.referralCount || 0,
          ambassadorSince: user.ambassadorSince || null,
          badges: determineUserBadges(user)
        }));
      // Append seed/demo members — placeholders shown until enough real members
      // join. Remove these in src/data/seedMembers.js as real membership grows.
      const seedMembers = SEED_MEMBERS.map((seed) => ({
        id: seed.id,
        name: `${seed.firstName || ''} ${seed.lastName || ''}`.trim(),
        title: seed.title || '',
        company: seed.company || '',
        location: seed.location || '',
        specialty: seed.specialty || '',
        tier: '',
        image: seed.image || '',
        memberSince: seed.memberSince || '',
        isFoundingMember: seed.isFoundingMember || false,
        isVipMember: seed.isVipMember || false,
        role: seed.role || '',
        referralCount: seed.referralCount || 0,
        ambassadorSince: seed.ambassadorSince || null,
        badges: determineUserBadges(seed),
      }));
      const seedHidden = await getSeedContentHidden();
      setMembers(seedHidden ? approvedMembers : [...approvedMembers, ...seedMembers]);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filter dropdowns
  const locations = [...new Set(members.map(m => m.location).filter(Boolean))];

  // Define specialties
  const specialties = [
    'Wealth Manager',
    'Management Consultant',
    'Valuation',
    'Investment Banking',
    'Private Equity',
    'Venture Capital',
    'Law Firm',
    'Accounting',
    'Financial Planning',
    'Corporate Development',
    'Healthcare Finance'
  ];

  // Filter members based on search and filters
  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchTerm ||
      (member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.company || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = locationFilter === '' || member.location === locationFilter;
    const matchesSpecialty = specialtyFilter === '' || member.specialty === specialtyFilter;

    return matchesSearch && matchesLocation && matchesSpecialty;
  });

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Member Directory</h1>
          </div>
        </div>
      </div>

      {/* Directory Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  placeholder="Search members by name, title, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${
                    viewMode === 'grid'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md ${
                    viewMode === 'list'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="location-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Location
                </label>
                <select
                  id="location-filter"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Locations</option>
                  {locations.map((location, index) => (
                    <option key={index} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="specialty-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Specialty
                </label>
                <select
                  id="specialty-filter"
                  value={specialtyFilter}
                  onChange={(e) => setSpecialtyFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Specialties</option>
                  {specialties.map((specialty, index) => (
                    <option key={index} value={specialty}>{specialty}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-xl text-red-600">{error}</p>
              <button onClick={loadMembers} className="mt-4 bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700">
                Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Results Count */}
              <div className="mb-6">
                <p className="text-gray-600">
                  Showing {Math.min(currentPage * membersPerPage, filteredMembers.length)} of {filteredMembers.length} members
                </p>
              </div>

              {/* Member List */}
              {filteredMembers.length > 0 ? (
                viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {filteredMembers
                      .slice((currentPage - 1) * membersPerPage, currentPage * membersPerPage)
                      .map(member => (
                      <div key={member.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                        <div className="p-6 text-center">
                          <div className="w-24 h-24 rounded-full bg-gray-300 mx-auto mb-4 overflow-hidden">
                            {member.image ? (
                              <img src={member.image} alt={member.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <span className="text-gray-600 text-2xl font-bold">{(member.name || '?')[0]}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <h3 className="text-lg font-bold">{member.name}</h3>
                            {member.badges && member.badges.length > 0 && (
                              <BadgeList badges={member.badges} size="small" />
                            )}
                          </div>
                          {member.title && <p className="text-gray-600 text-sm mb-2">{member.title}</p>}
                          {member.company && <p className="text-gray-600 text-sm mb-4">{member.company}</p>}
                          <div className="space-y-1 text-sm text-gray-600 mb-4">
                            {member.location && <p>{member.location}</p>}
                            {member.specialty && <p>Specialty: {member.specialty}</p>}
                          </div>
                          <Link
                            to={`/directory/${member.id}`}
                            className="block w-full bg-apn-blue text-white text-center py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                          >
                            View Profile
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredMembers
                      .slice((currentPage - 1) * membersPerPage, currentPage * membersPerPage)
                      .map(member => (
                      <div key={member.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                        <div className="p-6 flex items-center">
                          <div className="w-16 h-16 rounded-full bg-gray-300 mr-6 flex-shrink-0 overflow-hidden">
                            {member.image ? (
                              <img src={member.image} alt={member.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <span className="text-gray-600 text-xl font-bold">{(member.name || '?')[0]}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-grow">
                            <div className="flex items-center gap-1">
                              <h3 className="text-lg font-bold">{member.name}</h3>
                              {member.badges && member.badges.length > 0 && (
                                <BadgeList badges={member.badges} size="small" />
                              )}
                            </div>
                            <p className="text-gray-600">
                              {member.title}{member.title && member.company ? ' at ' : ''}{member.company}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {member.location && (
                                <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                                  {member.location}
                                </span>
                              )}
                              {member.specialty && (
                                <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                                  {member.specialty}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <Link
                              to={`/directory/${member.id}`}
                              className="inline-block bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                            >
                              View Profile
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <p className="text-xl text-gray-600">No members found matching your search criteria.</p>
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {filteredMembers.length > membersPerPage && (
            <div className="mt-12 flex justify-center">
              <nav className="inline-flex rounded-md shadow">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-l-md border border-gray-300 ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Previous
                </button>

                {/* Generate page buttons */}
                {Array.from({ length: Math.ceil(filteredMembers.length / membersPerPage) }, (_, i) => i + 1)
                  .map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 border-t border-b border-gray-300 ${currentPage === pageNum ? 'bg-apn-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                      {pageNum}
                    </button>
                  ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredMembers.length / membersPerPage)))}
                  disabled={currentPage === Math.ceil(filteredMembers.length / membersPerPage)}
                  className={`px-4 py-2 rounded-r-md border border-gray-300 ${currentPage === Math.ceil(filteredMembers.length / membersPerPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Update Your Profile</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Keep your profile information up to date to make the most of your APN membership.
          </p>
          <Link to="/profile" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Edit Your Profile
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MemberDirectory;
